import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  AUTH_CONFIG,
  AUTH_ENDPOINTS,
  AUTH_STORAGE_KEYS,
  generateState,
  generateNonce,
  generateCodeVerifier,
  generateCodeChallenge
} from '../config/auth';

interface AuthUser {
  sub?: string;
  oid?: string;
  email?: string;
  name?: string;
  [key: string]: any;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoadingStuck: boolean;
  user: AuthUser | null;
  token: string | null;
  login: () => Promise<void>;
  logout: () => void;
  clearAuthCache: () => void;
  handleCallback: (code: string, state: string) => Promise<boolean>;
  refreshToken: () => Promise<boolean>;
  getUserId: () => string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [refreshInProgress, setRefreshInProgress] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loadingStartTime, setLoadingStartTime] = useState(Date.now());
  const [isLoadingStuck, setIsLoadingStuck] = useState(false);

  const clearAuthCache = useCallback(() => {
    try {
      localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(AUTH_STORAGE_KEYS.ID_TOKEN);
      localStorage.removeItem(AUTH_STORAGE_KEYS.USER_INFO);
      localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN_EXPIRY);

      sessionStorage.removeItem(AUTH_STORAGE_KEYS.STATE);
      sessionStorage.removeItem(AUTH_STORAGE_KEYS.NONCE);
      sessionStorage.removeItem(AUTH_STORAGE_KEYS.CODE_VERIFIER);

      const authRelatedKeys = [
        'auth_token', 'access_token', 'refresh_token', 'id_token',
        'user_info', 'token_expiry', 'auth_state', 'auth_nonce',
        'pkce_verifier', 'oauth_state', 'msal.token', 'azure_token'
      ];

      authRelatedKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        } catch (_e) {
          // Ignore errors for keys that don't exist
        }
      });

      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            if (cacheName.includes('auth') || cacheName.includes('token')) {
              caches.delete(cacheName);
            }
          });
        }).catch(() => {
          // Ignore cache clearing errors
        });
      }

    } catch (error) {
      console.error('Error clearing auth cache:', error);
    }

    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setIsLoading(false);
    setRefreshInProgress(false);
  }, []);

  const logout = useCallback(() => {
    clearAuthCache();

    const logoutUrl = `${AUTH_ENDPOINTS.logout}?post_logout_redirect_uri=${encodeURIComponent(AUTH_CONFIG.postLogoutRedirectUri)}`;
    window.location.href = logoutUrl;
  }, [clearAuthCache]);

  const decodeJWT = useCallback((token: string): any => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  }, []);

  const storeTokens = useCallback(async (tokens: Record<string, any>) => {
    try {
      if (tokens.access_token) {
        localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
      }

      if (tokens.refresh_token) {
        localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
      }

      if (tokens.id_token) {
        localStorage.setItem(AUTH_STORAGE_KEYS.ID_TOKEN, tokens.id_token);
        const userInfo = decodeJWT(tokens.id_token);
        localStorage.setItem(AUTH_STORAGE_KEYS.USER_INFO, JSON.stringify(userInfo));
        setUser(userInfo);
        
        setToken(tokens.id_token);
      } else if (tokens.access_token) {
        setToken(tokens.access_token);
      }

      const expiryTime = Math.floor(Date.now() / 1000) + (tokens.expires_in || 3600);
      localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN_EXPIRY, expiryTime.toString());

    } catch (error) {
      console.error('Error storing tokens:', error);
      throw error;
    }
  }, [decodeJWT]);

  const login = useCallback(async () => {
    try {
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const state = generateState();
      const nonce = generateNonce();

      sessionStorage.setItem(AUTH_STORAGE_KEYS.STATE, state);
      sessionStorage.setItem(AUTH_STORAGE_KEYS.NONCE, nonce);
      sessionStorage.setItem(AUTH_STORAGE_KEYS.CODE_VERIFIER, codeVerifier);

      const authParams = new URLSearchParams({
        client_id: AUTH_CONFIG.clientId,
        response_type: 'code',
        redirect_uri: AUTH_CONFIG.redirectUri,
        scope: AUTH_CONFIG.scopes.join(' '),
        state: state,
        nonce: nonce,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        response_mode: 'query'
      });

      const authUrl = `${AUTH_ENDPOINTS.authorize}?${authParams.toString()}`;
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error initiating login:', error);
      throw error;
    }
  }, []);

  const handleCallback = useCallback(async (code: string, state: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const storedState = sessionStorage.getItem(AUTH_STORAGE_KEYS.STATE);
      const storedCodeVerifier = sessionStorage.getItem(AUTH_STORAGE_KEYS.CODE_VERIFIER);
      
      if (state !== storedState) {
        throw new Error('Invalid state parameter');
      }

      if (!storedCodeVerifier) {
        throw new Error('Missing code verifier for PKCE');
      }

      const tokenParams: Record<string, string> = {
        client_id: AUTH_CONFIG.clientId,
        grant_type: 'authorization_code',
        code,
        redirect_uri: AUTH_CONFIG.redirectUri,
        code_verifier: storedCodeVerifier,
      };

      console.log('Token exchange request (PKCE):', { ...tokenParams, code_verifier: '[REDACTED]' });

      const tokenResponse = await fetch(AUTH_ENDPOINTS.token, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(tokenParams),
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.text();
        console.error('Token exchange failed:', {
          status: tokenResponse.status,
          statusText: tokenResponse.statusText,
          error: errorData
        });
        throw new Error(`Failed to exchange code for tokens: ${tokenResponse.status} ${errorData}`);
      }

      const tokens = await tokenResponse.json();
      await storeTokens(tokens);

      sessionStorage.removeItem(AUTH_STORAGE_KEYS.STATE);
      sessionStorage.removeItem(AUTH_STORAGE_KEYS.NONCE);
      sessionStorage.removeItem(AUTH_STORAGE_KEYS.CODE_VERIFIER);

      setIsAuthenticated(true);
      setIsLoading(false);

      return true;
    } catch (error) {
      console.error('Error handling OAuth callback:', error);
      setIsAuthenticated(false);
      setIsLoading(false);
      throw error;
    }
  }, [storeTokens]);

  useEffect(() => {
    const checkAuthStatus = () => {
      try {
        setLoadingStartTime(Date.now());
        setIsLoadingStuck(false);
        
        const storedAccessToken = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
        const storedIdToken = localStorage.getItem(AUTH_STORAGE_KEYS.ID_TOKEN);
        const tokenExpiry = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN_EXPIRY);
        const userInfo = localStorage.getItem(AUTH_STORAGE_KEYS.USER_INFO);

        const tokenToUse = storedIdToken || storedAccessToken;

        if (tokenToUse && tokenExpiry && userInfo) {
          const expiryTime = parseInt(tokenExpiry);
          const currentTime = Math.floor(Date.now() / 1000);

          if (currentTime < expiryTime) {
            setToken(tokenToUse);
            setUser(JSON.parse(userInfo));
            setIsAuthenticated(true);
            setIsLoading(false);
            return;
          } else {
            const refreshTokenValue = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
            if (refreshTokenValue) {
              setIsLoading(true);
              return;
            }
          }
        }

        setIsAuthenticated(false);
        setIsLoading(false);
      } catch (error) {
        console.error('Error checking auth status:', error);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      setIsLoadingStuck(false);
      return;
    }

    const checkLoadingDuration = () => {
      const loadingDuration = Date.now() - loadingStartTime;
      
      if (loadingDuration > 10000) {
        setIsLoadingStuck(true);
        window.dispatchEvent(new CustomEvent('auth-loading-stuck', {
          detail: { duration: loadingDuration }
        }));
      }
    };

    const interval = setInterval(checkLoadingDuration, 1000);
    return () => clearInterval(interval);
  }, [isLoading, loadingStartTime]);

  const refreshTokenFn = useCallback(async (): Promise<boolean> => {
    if (refreshInProgress) return false;
    setRefreshInProgress(true);
    try {
      const refreshTokenValue = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
      if (!refreshTokenValue) throw new Error('No refresh token available');

      const refreshParams: Record<string, string> = {
        client_id: AUTH_CONFIG.clientId,
        grant_type: 'refresh_token',
        refresh_token: refreshTokenValue,
      };

      const response = await fetch(AUTH_ENDPOINTS.token, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(refreshParams),
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Token refresh failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(`Failed to refresh token: ${response.status} ${errorData}`);
      }

      const tokens = await response.json();
      await storeTokens(tokens);

      setIsAuthenticated(true);
      setIsLoading(false);
      return true;
    } catch (error) {
      console.error('Error refreshing token:', error);
      logout();
      return false;
    } finally {
      setRefreshInProgress(false);
    }
  }, [refreshInProgress, logout, storeTokens]);

  useEffect(() => {
    const interval = setInterval(() => {
      const expiryTime = parseInt(localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN_EXPIRY) || '0');
      const currentTime = Math.floor(Date.now() / 1000);
      if (expiryTime && expiryTime - currentTime < 120) {
        refreshTokenFn();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [refreshTokenFn]);

  const getUserId = useCallback((): string => {
    if (user) {
      return user.sub || user.oid || user.email || 'authenticated_user';
    }
    return 'anonymous';
  }, [user]);

  const value: AuthContextValue = {
    isAuthenticated,
    isLoading,
    isLoadingStuck,
    user,
    token,
    login,
    logout,
    clearAuthCache,
    handleCallback,
    refreshToken: refreshTokenFn,
    getUserId,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
